"use client";
import React, { useEffect, useState } from "react";
import ReactModal from "react-modal";
import CloseIcon from "../_icons/closeIcon";
import {
  ScheduleDateType,
  ScheduledDateDropdown,
} from "../scheduledDateDropdown/scheduledDateDropdown";
import {
  ScheduledTimeDropdown,
  ScheduleTimeType,
} from "../scheduledTimeDropdown/scheduledTimeDropdown";
import { OrderType } from "@/types";
import { DateTime, Duration } from "luxon";
import { ConfirmButton } from "../button/confirmButton";
import { isWithinBusinessHours } from "@/utils/restaurantCartUtils";

export const OnloadScheduledOrderModal = ({
  isOpen,
  onRequestClose,
  merchant,
  merchantColor,
}: any) => {
  const merchantService = merchant.services.find(
    (s: any) => s.type === "PICKUP" || s.type === "WEBSITE"
  );
  const curbsideService = merchant.services.find(
    (s: any) => s.type === "CURBSIDE"
  );
  const deliveryService = merchant.services.find(
    (s: any) => s.type === "DELIVERY"
  );
  const now = DateTime.now().setZone(merchant?.timezone);
  const orderType = (() => {
    if (typeof window !== "undefined") {
      const sessionTime = JSON.parse(
        localStorage.getItem("scheduledTime") || "{}"
      );
      const defaultOrderType = sessionTime?.orderType || "pickup";
      const restaurantServices = [
        { type: "pickup", service: merchantService },
        { type: "curbside", service: curbsideService },
        { type: "delivery", service: deliveryService },
      ];
      const enabledServices = restaurantServices.filter(
        ({ service }) => service?.enabled
      );

      if (enabledServices.length >= 1) {
        return enabledServices[0].type;
      }
      return defaultOrderType;
    }
    return "pickup";
  })();
  const [scheduleDate, setScheduleDate] = useState<ScheduleDateType>({
    displayValue: "Today",
    dateObject: now.toJSDate(),
  });
  const [scheduleTime, setScheduleTime] = useState<ScheduleTimeType>({
    displayValue: "",
    timeObject: new Date(now.toJSDate()),
  });

  const currentDateTime = DateTime.fromJSDate(now.toJSDate(), {});
  const nowInMerchantZone = currentDateTime.setZone(merchant.timezone);
  const leadTimeDuration = Duration.fromObject({
    minutes: merchant.leadTime,
  });
  const targetPickupTime = nowInMerchantZone.plus(leadTimeDuration);
  const businessHours = merchantService?.hours;
  const withinBusinessHours = isWithinBusinessHours(
    targetPickupTime,
    businessHours,
    merchant?.timezone,
    merchant?.leadTime
  );

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (withinBusinessHours) {
    return null;
  }

  return (
    <ReactModal
      ariaHideApp={false}
      appElement={global?.window?.document?.getElementById("app") || undefined}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      className="fixed inset-0 flex items-center justify-center"
      overlayClassName="fixed inset-0 bg-black bg-opacity-75 z-50"
      shouldCloseOnOverlayClick={true}
    >
      <div className="relative bg-white sm:rounded-lg shadow-lg w-full max-w-[600px] h-full sm:h-[340px] sm:w-[55%] mx-auto overflow-visible">
        <button
          onClick={onRequestClose}
          className="absolute top-5 right-5 text-gray-600 hover:text-gray-900 z-10"
        >
          <CloseIcon />
        </button>
        <div className="flex flex-col h-full px-8 justify-between">
          <div>
            <h2 className="text-#373737 font-semibold text-2xl pt-4 pr-14">
              {merchant.name} isn&apos;t accepting online orders right now.
            </h2>
            <p className="pt-4">You can schedule an order for later. </p>
            <div className="py-12 flex flex-col sm:flex-row gap-4 w-full pb-6">
              <div className="w-full">
                <ScheduledDateDropdown
                  scheduleDate={scheduleDate}
                  setScheduleDate={setScheduleDate}
                  scheduleDateRange={merchant?.scheduledOrdering?.maxDays - 1}
                  setScheduleTime={setScheduleTime}
                  businessHours={businessHours}
                  timezone={merchant.timezone}
                />
              </div>
              <div className="w-full">
                <ScheduledTimeDropdown
                  scheduleTime={scheduleTime}
                  setScheduleTime={setScheduleTime}
                  businessHours={businessHours}
                  scheduleDate={scheduleDate}
                  leadTime={merchant.leadTime}
                  timezone={merchant.timezone}
                  orderType={orderType}
                />
              </div>
            </div>
          </div>
          <div>
            <ConfirmButton
              style={{ backgroundColor: merchantColor }}
              className="px-16 py-5 w-full text-xl font-medium leading-7 text-center text-white whitespace-nowrap rounded-lg mb-5"
              onClick={() => {
                global?.window?.localStorage.setItem(
                  "scheduledTime",
                  JSON.stringify({
                    orderType: orderType,
                    date: scheduleDate,
                    time: scheduleTime,
                  })
                );
                onRequestClose();
              }}
            >
              <p className="text-2xl font-medium">Schedule an order</p>
            </ConfirmButton>
          </div>
        </div>
      </div>
    </ReactModal>
  );
};
